import { Box, Checkbox, FormControl, FormControlLabel, Slider } from '@mui/material';
import React, { useEffect, useMemo } from 'react';

import { usePipelineBlockParams } from './usePipelineBlockParams';

import '@data/Simulations/pipeline/blocks/SolarParamsBlock';

import type { SolarParamsBlockParameters } from '@data/Simulations/pipeline/blocks/SolarParamsBlock';
import { ControllerManager } from '@gui/controller/ControllerManager';
import type { BlockParameters } from '@visualizer/types';

type SolarParamsProps = {
    blockId: string;
};

const formatPercentage = (value: number) => `${Math.round(value * 100)}`;

export const getSolarParamsDefault = (): Pick<
    SolarParamsBlockParameters,
    'isCloudCoverOn' | 'cloudCoverPercentage' | 'isDiffuseRadiationOn' | 'diffuseRadiationPercentage'
> => ({
    isCloudCoverOn: false,
    cloudCoverPercentage: 0,

    isDiffuseRadiationOn: false,
    diffuseRadiationPercentage: 0,
});

const SolarParamsParameters: React.FC<SolarParamsProps> = ({ blockId }) => {
    const params = usePipelineBlockParams<SolarParamsBlockParameters>(blockId);

    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);
    const updateParams = (partial: Partial<SolarParamsBlockParameters>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    // -----------------------------
    // PARAM INITIALIZATION
    // -----------------------------
    useEffect(() => {
        if (!params) {
            updateParams(getSolarParamsDefault());
            return;
        }

        const missing: Partial<SolarParamsBlockParameters> = {};

        if (params.isCloudCoverOn == null) missing.isCloudCoverOn = false;
        if (params.cloudCoverPercentage == null) missing.cloudCoverPercentage = 0;

        if (params.isDiffuseRadiationOn == null) missing.isDiffuseRadiationOn = false;
        if (params.diffuseRadiationPercentage == null) missing.diffuseRadiationPercentage = 0;

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params]);

    if (!params) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* ----------------------------- */}
            {/* CLOUD COVERAGE SECTION        */}
            {/* ----------------------------- */}
            <FormControl className="panel-compact-control-stack">
                <FormControlLabel
                    control={
                        <Checkbox checked={params.isCloudCoverOn} onChange={(e) => updateParams({ isCloudCoverOn: e.target.checked })} />
                    }
                    label="Cloud Coverage"
                />

                <Box sx={{ pl: 2, opacity: params.isCloudCoverOn ? 1 : 0.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1.5 }}>
                        <Slider
                            min={0}
                            max={1}
                            size="small"
                            className="panel-slider"
                            step={0.01}
                            sx={{ flexGrow: 1, mr: 1 }}
                            value={params.cloudCoverPercentage || 0}
                            disabled={!params.isCloudCoverOn}
                            onChange={(_, v) => updateParams({ cloudCoverPercentage: v as number })}
                            valueLabelDisplay="auto"
                            valueLabelFormat={formatPercentage}
                        />
                        <Box>[%]</Box>
                    </Box>
                </Box>
            </FormControl>

            {/* ----------------------------- */}
            {/* DIFFUSE RADIATION SECTION     */}
            {/* ----------------------------- */}
            <FormControl className="panel-compact-control-stack">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={params.isDiffuseRadiationOn}
                            onChange={(e) => updateParams({ isDiffuseRadiationOn: e.target.checked })}
                        />
                    }
                    label="Diffuse Radiation"
                />

                <Box sx={{ pl: 2, opacity: params.isDiffuseRadiationOn ? 1 : 0.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1.5 }}>
                        <Slider
                            size="small"
                            className="panel-slider"
                            min={0}
                            max={1}
                            step={0.01}
                            sx={{ flexGrow: 1, mr: 1 }}
                            value={params.diffuseRadiationPercentage || 0}
                            disabled={!params.isDiffuseRadiationOn}
                            onChange={(_, v) => updateParams({ diffuseRadiationPercentage: v as number })}
                            valueLabelDisplay="auto"
                            valueLabelFormat={formatPercentage}
                        />
                        <Box>[%]</Box>
                    </Box>
                </Box>
            </FormControl>
        </Box>
    );
};

export default SolarParamsParameters;
